import type { CanonicalResume } from '../schemas/resumeSchema';

export interface CanvasSectionRow {
  title: string;
  content: string;
}

/** Profile data that can be used to override canonical personalInfo. */
export interface ProfileOverride {
  displayName?: string | null;
  email?: string | null;
  phone?: string | null;
  location?: string | null;
  linkedinUrl?: string | null;
  githubUrl?: string | null;
}

/** Check if a value is a junk placeholder that shouldn't appear on a resume. */
function isJunkValue(val: string | undefined | null): boolean {
  if (!val) return true;
  const t = val.trim();
  if (!t) return true;
  // N/A, NA, Not Available, None, Null, etc.
  return /^(n\/a|na|not\s+available|not\s+provided|not\s+specified|none|null|undefined|unknown|n\.a\.|to\s+be\s+updated|tbd|\[.*\])$/i.test(t);
}

/** Check if a line looks like a work experience bullet (not personal info). */
function isWorkExperienceLine(line: string): boolean {
  const t = line.trim();
  // Starts with a bullet character
  const BULLET_CHARS_RE = /^[\u2022\u2023\u2043\u204C\u204D\u2219\u25AA\u25AB\u25B8\u25BA\u25CF\u25CB\u25E6\u2605\u2606\u2713\u2714\u2717\u2718\u27A2\u2981\u29BE\u29BF•●○◦▪▸►‣⁃∙⋅◾◽\-*]\s*/;
  if (BULLET_CHARS_RE.test(t)) {
    // Contains work experience action verbs
    return /\b(engineered|developed|led|managed|implemented|designed|built|created|delivered|achieved|drove|spearheaded|collaborated|established|improved|increased|reduced|launched|architected|integrated|optimized|automated|analyzed|executed|coordinated|directed|oversaw|mentored)\b/i.test(t);
  }
  // Long text with action verbs is likely work experience
  if (t.length > 50 && /\b(engineered|developed|implemented|designed|built|created|delivered|achieved|spearheaded|collaborated|architected|integrated)\b/i.test(t)) {
    return true;
  }
  return false;
}

function joinNonEmpty(parts: Array<string | undefined | null>, sep = ' | '): string {
  return parts
    .map((p) => (p ?? '').trim())
    .filter((p) => p && !isJunkValue(p))
    .join(sep);
}

/** Deterministic JSON → canvas rows. No regex parsing of unstructured resume text. */
export function canonicalResumeToCanvasSections(
  data: CanonicalResume,
  profileOverride?: ProfileOverride | null,
): CanvasSectionRow[] {
  const sections: CanvasSectionRow[] = [];

  // Always prefer profile data over canonical data for personal info
  // This prevents work experience bullets from corrupting the header
  const fullName = (profileOverride?.displayName?.trim() || data.personalInfo.fullName || '').trim();
  const email = profileOverride?.email?.trim() || data.personalInfo.email || '';
  const phone = profileOverride?.phone?.trim() || data.personalInfo.phone || '';
  const location = profileOverride?.location?.trim() || data.personalInfo.location || '';
  const linkedin = profileOverride?.linkedinUrl?.trim() || data.personalInfo.linkedin || '';
  const github = profileOverride?.githubUrl?.trim() || data.personalInfo.github || '';

  // Validate fullName is not a work experience bullet
  const validName = fullName && !isWorkExperienceLine(fullName) ? fullName : '';

  const personalLines = [
    validName,
    joinNonEmpty([email, phone, location]),
    linkedin,
    github,
  ].filter((line) => {
    const trimmed = (line ?? '').trim();
    return trimmed.length > 0 && !isJunkValue(trimmed) && !isWorkExperienceLine(trimmed);
  });

  if (personalLines.length) {
    sections.push({ title: 'Personal Information', content: personalLines.join('\n') });
  }

  if (data.professionalSummary.trim()) {
    sections.push({ title: 'Professional Summary', content: data.professionalSummary.trim() });
  }

  if (data.workExperience.length) {
    const blocks = data.workExperience.map((exp) => {
      const header = joinNonEmpty([
        exp.title,
        exp.company,
        exp.location,
        joinNonEmpty([exp.startDate, exp.endDate], ' – '),
      ]);
      const bullets = exp.highlights
        .map((h) => h.trim())
        .filter(Boolean)
        .map((h) => (h.startsWith('•') || h.startsWith('-') ? h : `• ${h}`));
      return [header, ...bullets].filter(Boolean).join('\n');
    });
    sections.push({ title: 'Work Experience', content: blocks.join('\n\n') });
  }

  if (data.education.length) {
    const edu = data.education
      .map((item) => joinNonEmpty([item.degree, item.institution, item.year], ' — '))
      .filter(Boolean);
    if (edu.length) {
      sections.push({ title: 'Education', content: edu.join('\n') });
    }
  }

  const skillLines = [
    data.skills.technical.length ? `Technical: ${data.skills.technical.join(', ')}` : '',
    data.skills.frameworksAndTools.length
      ? `Frameworks & Tools: ${data.skills.frameworksAndTools.join(', ')}`
      : '',
    data.skills.managementAndDomain.length
      ? `Domain Expertise: ${data.skills.managementAndDomain.join(', ')}`
      : '',
  ].filter(Boolean);
  if (skillLines.length) {
    sections.push({ title: 'Skills', content: skillLines.join('\n') });
  }

  if (data.certifications?.length) {
    sections.push({
      title: 'Certifications',
      content: data.certifications.map((c) => c.trim()).filter(Boolean).join('\n'),
    });
  }

  if (data.awards?.length) {
    sections.push({
      title: 'Awards',
      content: data.awards.map((a) => a.trim()).filter(Boolean).join('\n'),
    });
  }

  return sections.filter((s) => s.content.trim().length > 0);
}

export function canonicalResumeToPlainText(data: CanonicalResume): string {
  return canonicalResumeToCanvasSections(data)
    .map((s) => `${s.title}\n${s.content}`)
    .join('\n\n');
}
